import {useEffect, useState} from 'react';

import type {IObservableArray} from 'mobx';

import {
  action,
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import nextFrame from 'next-frame';

import type {CancellablePromise} from '@yourcoach/async-utils';

import type {ApiMethodName, Entity} from '../types';
import type {
  ApiRpcCursor,
  ApiRpcRequestParams,
  QueryOperator,
} from './apiRequest';

import {isTestEnv} from '../../utils/test-utils/isTestEnv';
import {apiRequest} from './apiRequest';
import {expandObj} from './expand';

const DEFAULT_LIMIT = 100;

export interface Options {
  appendTo?: 'begin' | 'end';
  cancelId?: string;
  override?: boolean;
  reverse?: boolean;
  silent?: boolean;
}

type Params<P extends object = ApiRpcRequestParams> = Omit<
  ConstructorParameters<typeof CollectionStore>[0],
  'params'
> & {
  params?: P;
};

export class CollectionStore<
  T extends Entity,
  P extends {[key: string]: any} = ApiRpcRequestParams,
> {
  count = 0;
  cursor: {
    next: ApiRpcCursor | null;
    prev: ApiRpcCursor | null;
  } = {
    next: null,
    prev: null,
  };
  error = null;
  hasMore: boolean = false;
  isFetching: boolean = false;
  isLoaded: boolean = false;
  items: T[] = [];
  private _fetchRequest: CancellablePromise<any> | null;
  private method?: ApiMethodName = undefined;
  private observableFields: string[] = [];
  // @ts-expect-error: TODO: describy why
  private params: P = {};
  private withAuth: boolean = true;
  private withCount: boolean = false;

  constructor({
    method,
    observableFields,
    params,
    withAuth,
    withCount,
  }: {
    method?: ApiMethodName;
    observableFields?: string[];
    params?: P;
    withAuth?: boolean;
    withCount?: boolean;
  }) {
    makeObservable(this, {
      // tread about jest.spy fails on configurable objects https://github.com/mobxjs/mobx/issues/2752
      addItem: !isTestEnv ? action.bound : false,
      clear: !isTestEnv ? action.bound : false,
      count: observable,
      cursor: observable,
      fetch: !isTestEnv ? action.bound : false,
      fetchCount: !isTestEnv ? action.bound : false,
      hasItems: computed,
      hasMore: observable,
      isFetching: observable,
      isLoaded: observable,
      items: observable.shallow,
      removeItem: !isTestEnv ? action.bound : false,
      updateItem: !isTestEnv ? action.bound : false,
    });

    this.method = method || this.method;
    this.withAuth = withAuth || this.withAuth;
    this.withCount = withCount || this.withCount;
    this.params = params || this.params;
    this.observableFields = observableFields || this.observableFields;
  }

  get hasItems() {
    return !!this.items.length;
  }

  get isEmpty() {
    return this.isLoaded && !this.hasItems;
  }

  addItem(item: T, index: number = this.items.length) {
    if (this.findItem(item)) {
      return;
    }

    this.items.splice(index, 0, item);

    this.count += 1;
  }

  clear() {
    /* istanbul ignore next */
    if (!isTestEnv && this.isFetching && this._fetchRequest) {
      this._fetchRequest.cancel();
    }

    this.isLoaded = false;
    this.isFetching = false;
    this.hasMore = false;
    this.count = 0;
    (this.items as IObservableArray).clear();
    this.error = null;
    this.cursor.prev = null;
    this.cursor.next = null;
  }

  // @ts-expect-error: TODO: describy why
  async fetch(fetchParams: P = {}, options: Options = {}) {
    try {
      if (!this.method) {
        throw new Error(
          'createCollectionStore: seems you forgot to set method',
        );
      }

      this.error = null;

      if (!options.silent) {
        this.isFetching = true;
      }

      const mergedParams = {
        ...this.params,
      };

      Object.keys(fetchParams).forEach(key => {
        const value = fetchParams[key];

        if (['query', 'sort'].includes(key)) {
          // @ts-expect-error: TODO: describy why
          mergedParams[key] = (mergedParams[key] || []).concat(value);
        } else {
          // @ts-expect-error: TODO: describy why
          mergedParams[key] = value;
        }
      });

      const useCursor = !!(mergedParams as ApiRpcRequestParams).cursor;

      if (!useCursor) {
        (mergedParams as ApiRpcRequestParams).limit =
          ((mergedParams as ApiRpcRequestParams).limit || DEFAULT_LIMIT) + 1;
      }

      /* istanbul ignore next */
      if (!isTestEnv && this.isFetching && this._fetchRequest) {
        this._fetchRequest.cancel();
      }

      this._fetchRequest = null;

      if (this.withCount) {
        const countParams = {...mergedParams};

        delete (countParams as ApiRpcRequestParams).cursor;
        delete (countParams as ApiRpcRequestParams).expand;
        delete (countParams as ApiRpcRequestParams).limit;
        delete (countParams as ApiRpcRequestParams).sort;
        delete (countParams as ApiRpcRequestParams).offset;

        if (Array.isArray((countParams as ApiRpcRequestParams).query)) {
          const UNSUPPORTED_OPERATORS = ['fulltext'] as QueryOperator[];

          (countParams as ApiRpcRequestParams).query = (
            countParams as ApiRpcRequestParams
          )
            // @ts-expect-error: TODO: describy why
            .query!.slice()
            .filter(
              query => query && !UNSUPPORTED_OPERATORS.includes(query[1]),
            );
        }

        this._fetchRequest = apiRequest<P>({
          batch: [
            {
              method: this.method,
              params: mergedParams,
            },
            {
              method: this.method
                .split('.')
                .slice(0, -1)
                .concat(['count'])
                .join('.') as ApiMethodName,
              params: countParams,
            },
          ],
          options: {
            cancelId: options.cancelId,
            withAuth: this.withAuth,
          },
        });
      } else {
        this._fetchRequest = apiRequest<P>({
          method: this.method,
          options: {
            withAuth: this.withAuth,
          },
          params: mergedParams,
        });
      }

      const result = await this._fetchRequest;

      let items: T[] = [];

      const itemsResult: any = this.withCount ? result[0] : result;

      const itemsLength = itemsResult._items.length;

      for (let i = 0; i < itemsLength; i += 1) {
        await nextFrame();

        let item: T;

        if ((mergedParams as ApiRpcRequestParams).expand) {
          item = expandObj<T>(itemsResult._items[i], {
            expand: (mergedParams as ApiRpcRequestParams).expand!,
            expanded: itemsResult._expanded,
          });
        } else {
          item = itemsResult._items[i];
        }

        items[i] = this.observeEntityFields(item);
      }

      runInAction(() => {
        const hasOffset =
          (mergedParams as ApiRpcRequestParams).offset !== undefined;

        const hasMore =
          items.length === (mergedParams as ApiRpcRequestParams).limit;

        if (hasMore && !useCursor) {
          items.pop();
        }

        if (useCursor) {
          if (!options.override) {
            if ((mergedParams as ApiRpcRequestParams).cursor![1] === 1) {
              this.cursor.next = itemsResult._next;
            } else if (
              (mergedParams as ApiRpcRequestParams).cursor![1] === -1
            ) {
              this.cursor.prev = itemsResult._prev;
            } else {
              this.cursor.next = itemsResult._next;
              this.cursor.prev = itemsResult._prev;
            }
          } else {
            this.cursor.next = itemsResult._next;
            this.cursor.prev = itemsResult._prev;
          }
        } else {
          this.hasMore = hasMore;
        }

        if (this.withCount) {
          this.count = result[1]._count;
        } else {
          this.count = 0;
        }

        if (options.override || (!hasOffset && !options.appendTo)) {
          (this.items as IObservableArray).clear();
        }

        if (options.reverse) {
          items = items.reverse();
        }

        if (options.appendTo === 'end' || !options.appendTo) {
          this.items.push(...items);
        }

        if (options.appendTo === 'begin') {
          this.items.unshift(...items);
        }

        this.isLoaded = true;

        if (!options.silent) {
          this.isFetching = false;
        }
      });

      return items;
    } catch (error) {
      this.error =
        this.withCount && Array.isArray(error)
          ? error.find((item: any) => apiRequest.isRpcRequestError(item))
          : error;

      runInAction(() => {
        if (apiRequest.isCanceledError(error)) {
          if (!options.silent) {
            this.isFetching = false;
          }
        } else {
          this.isLoaded = true;

          if (!options.silent) {
            this.isFetching = false;
          }
        }
      });

      throw this.error;
    }
  }

  async fetchCount(fetchParams: ApiRpcRequestParams = {}) {
    let params: P | undefined;

    if (Object.keys(this.params).length) {
      params = {
        ...this.params,
      };

      Object.keys(fetchParams).forEach(key => {
        const value = fetchParams[key];

        if (['query', 'sort'].includes(key)) {
          // @ts-expect-error: TODO: describy why
          params![key] = (params![key] || []).concat(value);
        } else {
          // @ts-expect-error: TODO: describy why
          params![key] = value;
        }
      });

      delete (params as ApiRpcRequestParams).cursor;
      delete (params as ApiRpcRequestParams).expand;
      delete (params as ApiRpcRequestParams).limit;
      delete (params as ApiRpcRequestParams).sort;
      delete (params as ApiRpcRequestParams).offset;

      if ((params as ApiRpcRequestParams).query) {
        const UNSUPPORTED_OPERATORS = ['fulltext'] as QueryOperator[];

        (params as ApiRpcRequestParams).query = (params as ApiRpcRequestParams)
          // @ts-expect-error: TODO: describy why
          .query!.slice()
          .filter(query => query && !UNSUPPORTED_OPERATORS.includes(query[1]));
      }
    }

    const result = await apiRequest<P>({
      method: this.method
        ?.split('.')
        .slice(0, -1)
        .concat(['count'])
        .join('.') as ApiMethodName,
      params,
    });

    const count = result._count;

    runInAction(() => {
      this.count = count;
    });

    return count;
  }

  findItem(item: T) {
    return this.items.find(x => x._id === item._id);
  }

  removeItem(item: T) {
    const index = this.items.findIndex(x => x._id === item._id);

    if (index >= 0) {
      this.items.splice(index, 1);

      this.count -= 1;
    }
  }

  setMethod(value: ApiMethodName) {
    this.method = value;
  }

  setWithAuth(value: boolean) {
    this.withAuth = Boolean(value);
  }

  setWithCount(value: boolean) {
    this.withCount = Boolean(value);
  }

  updateItem(item: T, data: any) {
    const index = this.items.findIndex(x => x._id === item._id);

    if (index >= 0) {
      this.items[index] = {
        ...this.items[index],
        ...data,
      };
    }
  }

  private observeEntityFields(item: T) {
    if (this.observableFields.length) {
      item = makeObservable(
        item,
        this.observableFields.reduce(
          (obj, field) => ({
            ...obj,
            [field]: Array.isArray(item[field])
              ? observable.shallow
              : observable,
          }),
          {},
        ),
      );
    }

    return item;
  }
}

export const createCollectionStore = <
  T extends Entity,
  P extends object = ApiRpcRequestParams,
>(
  params: Params<P>,
) => {
  return new CollectionStore<T, P>(params);
};

/**
 * @knipignore
 */
export const useCollectionStore = <
  T extends Entity,
  P extends object = ApiRpcRequestParams,
>(
  params: Params<P>,
) => {
  const [store] = useState(() => createCollectionStore<T, P>(params));

  const [items, setItems] = useState(store.items);
  const [hasItems, setHasItems] = useState(store.hasItems);
  const [count, setCount] = useState(store.count);
  const [hasMore, setHasMore] = useState(store.hasMore);
  const [cursor, setCursor] = useState(store.cursor);
  const [isFetching, setIsFetching] = useState(store.isLoaded);
  const [isLoaded, setIsLoaded] = useState(store.isFetching);
  const [isEmpty, setIsEmpty] = useState(store.isEmpty);
  const [error, setError] = useState(store.error);

  useEffect(() => {
    const dispose = autorun(() => {
      setItems(store.items.slice());
      setHasItems(store.hasItems);
      setCount(store.count);
      setHasMore(store.hasMore);
      setCursor(store.cursor);
      setIsFetching(store.isFetching);
      setIsLoaded(store.isLoaded);
      setIsEmpty(store.isEmpty);
      setError(store.error);
    });

    return () => {
      dispose();

      store.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    count,
    cursor,
    error,
    hasItems,
    hasMore,
    isEmpty,
    isFetching,
    isLoaded,
    items,
    store,
  };
};
